import {
  useCreateTourThemeMutation,
  useGetTourThemesQuery,
  useUpdateAllTourStepsMutation,
  useUpdateTourDraftSettingsMutation,
} from '@carboncopyco/data';
import { PaletteOutlineIcon } from '@carboncopyco/icons';
import type { TourStepTooltipConfig } from '@carboncopyco/types';
import {
  Button,
  InputText,
  Modal,
  TextBase,
  TextCaption,
  TextSubHeader,
} from '@carboncopyco/ui';
import {
  camelCaseToUnderscore,
  getObjectPropertyPathsWithValues,
} from '@carboncopyco/utils';
import { Field, Form, Formik, type FormikState } from 'formik';
import { cloneDeep, unset } from 'lodash-es';
import { useEffect, useRef } from 'react';

const updateTooltipButtonsConfig = (
  tourThemeConfig: TourStepTooltipConfig,
  isTourLastStep: boolean
) => {
  if (isTourLastStep) {
    tourThemeConfig.buttons.nextButton.colorAndShape =
      tourThemeConfig.buttons.lastStepButton.colorAndShape;
    tourThemeConfig.buttons.nextButton.text =
      tourThemeConfig.buttons.lastStepButton.text;
  } else {
    tourThemeConfig.buttons.lastStepButton.colorAndShape =
      tourThemeConfig.buttons.nextButton.colorAndShape;
    tourThemeConfig.buttons.lastStepButton.text =
      tourThemeConfig.buttons.nextButton.text;
  }
};

export type CreateTourThemeModalProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  workspaceId: string;
  tourId: string;
  tooltipConfig: TourStepTooltipConfig;
  isTourLastStep: boolean;
};

type FormState = {
  themeName: string;
};

const TOOLTIP_CONFIG_IGNORE_PATHS = [
  'navigationOptions',
  'balloon.layout',
  'balloon.layoutBeforeConverting',
  'buttons.lastStepButton.action',
  'buttons.lastStepButton.label',
  'buttons.nextButton.action',
  'buttons.nextButton.label',
  'buttons.previousButton.action',
  'buttons.previousButton.label',
];

export const CreateTourThemeModal = ({
  open,
  onClose,
  organizationId,
  tourId,
  tooltipConfig,
  isTourLastStep,
  workspaceId,
}: CreateTourThemeModalProps) => {
  const nameInput = useRef<HTMLInputElement>(null);
  const { data: tourThemes } = useGetTourThemesQuery({
    organizationId,
  });
  const [createTourTheme] = useCreateTourThemeMutation();
  const [updateAllTourSteps] = useUpdateAllTourStepsMutation();
  const [updateTourDraftSettings] = useUpdateTourDraftSettingsMutation();

  useEffect(() => {
    if (open) {
      nameInput.current?.focus();
    }
  }, [open]);

  const submitForm = ({
    values,
    setFieldError,
    resetForm,
    applyTheme = true,
  }: {
    values: FormState;
    setFieldError: (field: string, errMsg: string) => void;
    resetForm: (values?: Partial<FormikState<FormState>>) => void;
    applyTheme?: boolean;
  }) => {
    const trimmedName = values.themeName.trim();

    if (!trimmedName) {
      return setFieldError('themeName', 'Name is required');
    }

    if (
      tourThemes.some(
        (theme) => theme.name.toLowerCase() === trimmedName.toLowerCase()
      )
    ) {
      return setFieldError('themeName', 'Name already exists');
    }

    const tourThemeConfig: TourStepTooltipConfig = cloneDeep(tooltipConfig);

    /**
     * According to the step, we need to update config of buttons from other steps.
     * For last step, we need to change config of next button for other steps.
     * For steps that are not last, we need to change config of last step button
     */
    updateTooltipButtonsConfig(tourThemeConfig, isTourLastStep);

    TOOLTIP_CONFIG_IGNORE_PATHS.forEach((path) => unset(tourThemeConfig, path));

    createTourTheme({
      organizationId,
      tourTheme: {
        name: trimmedName,
        tooltipConfig: tourThemeConfig,
        category: 'Published themes',
      },
    })
      .unwrap()
      .then((tourTheme) => {
        onClose();
        resetForm();
        if (applyTheme) {
          const updates = getObjectPropertyPathsWithValues(
            { config: { tooltip: tourThemeConfig } },
            camelCaseToUnderscore
          );

          updateAllTourSteps({
            organizationId,
            tourId: Number(tourId),
            updates,
          });
          updateTourDraftSettings({
            id: Number(tourId),
            organizationId,
            draftSettings: { tourThemeId: tourTheme.id },
          });
        }
      });
  };

  return (
    <Formik
      initialValues={{ themeName: '' }}
      onSubmit={(values, { setFieldError, resetForm }) => {
        submitForm({ values, setFieldError, resetForm });
      }}
    >
      {({ values, setFieldError, resetForm }) => (
        <Modal
          open={open}
          onClose={onClose}
          title={
            <div className="flex">
              <div className="mr-2.5">
                <PaletteOutlineIcon />
              </div>
              <TextSubHeader
                color="text-ds-natural-600"
                label="Save tooltip’s style as a theme"
              />
            </div>
          }
        >
          <Form>
            <div className="pt-5 pb-2.5">
              <TextBase
                label="Make a stylish, reusable theme based on your tooltip design."
                color="text-ds-natural-600"
              />
            </div>
            <Field name="themeName">
              {({ field, meta }) => {
                const error = meta.touched && meta.error;

                return (
                  <>
                    <InputText
                      ref={nameInput}
                      {...field}
                      state={error ? 'error' : 'normal'}
                    />
                    <div className="whitespace-nowrap h-6 flex items-center">
                      {error && (
                        <TextCaption
                          label={error}
                          color="text-ds-danger-400"
                          lineHeight="leading-ds-100"
                        />
                      )}
                    </div>
                  </>
                );
              }}
            </Field>

            <div className="flex justify-end">
              <Button
                onClick={onClose}
                dark
              >
                Cancel
              </Button>
              <div className="ml-2.5">
                <Button
                  onClick={() =>
                    submitForm({
                      values,
                      setFieldError,
                      resetForm,
                      applyTheme: false,
                    })
                  }
                  secondary
                >
                  Save theme
                </Button>
              </div>
              <div className="ml-2.5">
                <Button
                  onClick={() =>
                    submitForm({
                      values,
                      setFieldError,
                      resetForm,
                    })
                  }
                >
                  Save & Apply
                </Button>
              </div>
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
